import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Pick a WhatsApp template, see the exact message for the client, send — to
// one client or many. Nothing is sent until the person presses Send.
const S = {
  back: { position: 'fixed', inset: 0, background: 'rgba(17,24,39,.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '24px 12px' },
  modal: { background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '520px', padding: '20px' },
  lbl: { display: 'block', fontSize: '14px', fontWeight: 700, color: '#374151', margin: '14px 0 6px' },
  in: { width: '100%', padding: '12px 14px', border: '1.5px solid #d1d5db', borderRadius: '10px', fontSize: '16px', boxSizing: 'border-box', background: '#fff' },
  bubble: { background: '#dcf8c6', borderRadius: '10px', padding: '12px 14px', fontSize: '14px', whiteSpace: 'pre-wrap', lineHeight: 1.5 },
  send: { width: '100%', padding: '15px', borderRadius: '12px', border: 'none', background: '#25D366', color: '#fff', fontSize: '18px', fontWeight: 800, cursor: 'pointer', marginTop: '16px' },
  cancel: { width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d1d5db', background: '#fff', fontSize: '15px', cursor: 'pointer', marginTop: '8px' },
  warn: { background: '#fffbeb', color: '#92400e', padding: '10px 12px', borderRadius: '10px', fontSize: '14px', marginTop: '10px' },
  err: { background: '#fef2f2', color: '#b91c1c', padding: '10px 12px', borderRadius: '10px', fontSize: '14px', marginTop: '10px' },
};
const STATUS = { APPROVED: '', PENDING: ' (waiting for WhatsApp approval)', REJECTED: ' (rejected by WhatsApp)', NOT_SUBMITTED: ' (not submitted)', UNKNOWN: '' };

const SendWhatsApp = ({ apiBase, clients, onClose }) => {
  // clients: [{id, name}]
  const [templates, setTemplates] = useState([]);
  const [tid, setTid] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);
  const one = clients.length === 1;

  useEffect(() => {
    axios.get(`${apiBase}/wa-templates`).then((r) => {
      setTemplates(r.data || []);
      const first = (r.data || []).find((t) => t.status === 'APPROVED') || (r.data || [])[0];
      if (first) setTid(String(first.id));
    }).catch(() => setErr('Could not load the message templates.'));
  }, [apiBase]);

  useEffect(() => {
    if (!tid || !clients.length) return;
    setPreview(null);
    axios.post(`${apiBase}/wa-templates/preview`, { template_id: Number(tid), client_id: clients[0].id })
      .then((r) => setPreview(r.data)).catch(() => setPreview(null));
  }, [apiBase, tid, clients]);

  const t = templates.find((x) => String(x.id) === tid);
  const send = async () => {
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${apiBase}/wa-templates/send`, { template_id: Number(tid), client_ids: clients.map((c) => c.id) });
      setResult(r.data);
    } catch (e) {
      setErr(e.response?.status === 403 ? 'You are not allowed to send messages to clients.' : (e.response?.data?.error || 'Could not send. Check the internet and try again.'));
    } finally { setBusy(false); }
  };

  return (
    <div style={S.back} onClick={() => !busy && onClose(!!result)}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 800, margin: 0 }}>💬 Send WhatsApp</h2>
          <button onClick={() => onClose(!!result)} style={{ border: 'none', background: 'none', fontSize: '28px', cursor: 'pointer', color: '#6b7280' }} aria-label="Close">×</button>
        </div>
        <div style={{ fontSize: '14px', color: '#6b7280' }}>
          {one ? `To ${clients[0].name}${preview?.to ? ` · ${preview.to}` : ''}` : `To ${clients.length} clients`} · from The Real Connect (98200 76730)
        </div>

        {result ? (
          <div>
            <div style={{ textAlign: 'center', padding: '14px 0 4px' }}>
              <div style={{ fontSize: '48px' }}>{result.sent ? '✅' : '⚠️'}</div>
              <div style={{ fontSize: '19px', fontWeight: 800 }}>{result.sent} of {result.results.length} sent</div>
            </div>
            <div style={{ maxHeight: '260px', overflowY: 'auto', marginTop: '10px' }}>
              {result.results.map((r) => (
                <div key={r.client_id} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '14px', padding: '7px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <span>{r.name || `#${r.client_id}`}</span>
                  <span style={{ color: r.status === 'sent' ? '#16a34a' : r.status === 'failed' ? '#dc2626' : '#92400e', textAlign: 'right' }}>
                    {r.status === 'sent' ? 'Sent' : `${r.status === 'failed' ? 'Failed' : 'Skipped'}${r.reason ? ` — ${r.reason}` : ''}`}
                  </span>
                </div>
              ))}
            </div>
            <button style={S.cancel} onClick={() => onClose(true)}>Done</button>
          </div>
        ) : (
          <>
            <label style={S.lbl}>Message</label>
            <select style={S.in} value={tid} onChange={(e) => setTid(e.target.value)}>
              {templates.map((x) => <option key={x.id} value={x.id} disabled={x.status !== 'APPROVED' && x.status !== 'UNKNOWN'}>{x.label}{STATUS[x.status] ?? ` (${x.status})`}</option>)}
            </select>
            <label style={S.lbl}>{one ? 'What they will receive' : `Preview (for ${clients[0].name}; each client gets their own name)`}</label>
            <div style={S.bubble}>{preview ? preview.text : 'Loading preview…'}</div>
            {preview?.missing?.length > 0 && <div style={S.warn}>Can't send {one ? 'yet' : `to ${clients[0].name}`}: missing {preview.missing.join(', ')}.{!one && ' Clients with missing details are skipped.'}</div>}
            {t && t.status !== 'APPROVED' && t.status !== 'UNKNOWN' && <div style={S.warn}>This message is waiting for WhatsApp to approve it. You can send it once approved.</div>}
            {err && <div style={S.err}>{err}</div>}
            <button style={{ ...S.send, opacity: busy || !t || t.status !== 'APPROVED' ? 0.5 : 1 }} disabled={busy || !t || t.status !== 'APPROVED'} onClick={send}>
              {busy ? 'Sending…' : one ? 'Send' : `Send to ${clients.length} clients`}
            </button>
            <button style={S.cancel} onClick={() => onClose(false)} disabled={busy}>Cancel</button>
          </>
        )}
      </div>
    </div>
  );
};

export default SendWhatsApp;
